import type { Landmark } from '../../../data/types';
import type { LinearRGB } from '../../../core/color';
import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { boxSolid, horizontalCap, octahedronSolid, planPoint, prismSolid, pyramidOverRing, ringPoints } from '../solids';
import { gableRoof } from '../roofSolids';
import { defensiveMasonryFace } from '../fortifications/towerMasonry';
import { archedBlock } from './fortress/fortressBlocks';
import { buildVessel, vesselOutline, VESSEL } from './churchVessel';
import type { VesselPlan } from './churchModel';
import { centredWindow, rectWall } from './masonry';
import { meshFrameOf, planBounds, planFrameOf, toLayout } from './planFrame';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

export interface LeprozenhuisColors extends PaintedChapelColors {
  readonly tile: LinearRGB;
  readonly timber: LinearRGB;
  readonly garden: LinearRGB;
}

interface Rect { readonly u0: number; readonly u1: number; readonly v0: number; readonly v1: number; }
const ringOf = (r: Rect): Ring => [[r.u0, r.v0], [r.u1, r.v0], [r.u1, r.v1], [r.u0, r.v1]];

/** The period painting gives an enclosed hospital with separate chapel and ward roofs,
 * not four identical hipped ranges. The layout's 40×30 m complete reservation is retained.
 * The inset leaves room for its moat; ranges, gate location and all elevations are estimates. */
export function leprozenhuisPlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, landmark.heading), b = planBounds(plan);
  const centre: Vec2 = [(b.u0 + b.u1) / 2, (b.v0 + b.v1) / 2];
  const scale = Math.min((b.u1 - b.u0) / 40, (b.v1 - b.v0) / 30);
  const rect = (u0: number, u1: number, v0: number, v1: number): Rect => ({
    u0: centre[0] + u0 * scale, u1: centre[0] + u1 * scale,
    v0: centre[1] + v0 * scale, v1: centre[1] + v1 * scale });
  const wards = [rect(-15.5, 15.5, -10.8, -5.5), rect(10.2, 15.5, -5.5, 8.0)];
  const gate = rect(-17, -14.5, -3, 3);
  const walls = [rect(-17, -16.4, -12, -3), rect(-17, -16.4, 3, 12),
    rect(-17, 17, 11.4, 12), rect(16.4, 17, -12, 12), rect(-17, 17, -12, -11.4)];
  const chapel: VesselPlan = { u0: centre[0] - 12 * scale, chordU: centre[0] + 6.0 * scale,
    vc: centre[1] + 6 * scale, half: 3.2 * scale, eaveY: 9.0,
    ridgeY: 12.8, apse: 'polygonal' };
  return { plan, frame: meshFrameOf(plan), scale, centre, wards, gate, walls, chapel,
    passage: [centre[1] - 1.4 * scale, centre[1] + 1.4 * scale] as const };
}

function ward(builder: MeshBuilder, wall: VertexStyle, dark: VertexStyle, roof: VertexStyle,
  frame: Frame, rect: Rect, height: number, ridge: number): void {
  const ring = ringOf(rect);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % 4]!, width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const count = Math.max(1, Math.round(width / 4.5));
    const holes = [2.0, 4.25].flatMap((y) => Array.from({ length: count }, (_, n) => ({
      x: width * (n + 0.5) / count - 0.35, y, width: 0.7,
      height: y === 2 ? 1.45 : 1.1, cuts: [] })));
    defensiveMasonryFace(builder, wall, frame, a, b, 0, height, holes, dark);
  });
  const alongU = rect.u1 - rect.u0 >= rect.v1 - rect.v0;
  if (alongU) gableRoof(builder, { wall, roof }, frame,
    { ...rect, eaveY: height, ridgeY: ridge, ends: { start: true, end: true } });
  else {
    const turned = { u0: rect.v0, u1: rect.v1, v0: -rect.u1, v1: -rect.u0 };
    gableRoof(builder, { wall, roof }, { origin: frame.origin, yaw: frame.yaw + Math.PI / 2 },
      { ...turned, eaveY: height, ridgeY: ridge, ends: { start: true, end: true } });
  }
}

export function buildLeprozenhuis(builder: MeshBuilder, landmark: Landmark,
  colors: LeprozenhuisColors, ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY): number {
  const before = builder.triangleCount, p = leprozenhuisPlan(landmark);
  const styles = paintedChapelStyles(colors, ids, tracery);
  const tile: VertexStyle = { ...styles.roof, color: colors.tile, surface: SURFACE.roofTile };
  ward(builder, styles.wall, styles.opening, tile, p.frame, p.wards[0]!, 6.2, 9.4);
  ward(builder, styles.wall, styles.opening, tile, p.frame, p.wards[1]!, 5.6, 8.4);
  buildVessel(builder, styles, p.frame, p.chapel,
    { longWindows: { sill: 2.3, top: 7.7 }, westWindow: true, bay: 4.5 });
  for (const r of p.walls) boxSolid(builder, styles.wall, p.frame,
    [r.u0, 0, -r.v1], [r.u1, 3.2, -r.v0], { top: true, bottom: false });
  archedBlock(builder, styles.wall, p.frame, { u: [p.gate.u0, p.gate.u1],
    v: [p.gate.v0, p.gate.v1], foot: -0.1, top: 5.2,
    passage: { v: p.passage, springing: 2.25 }, openTop: true });
  gableRoof(builder, { wall: styles.wall, roof: tile }, p.frame,
    { ...p.gate, eaveY: 5.2, ridgeY: 7.4, ends: { start: true, end: true } });
  // The chapel's small openable belfry is a separate silhouette, not a tower on every corner.
  const centre: Vec2 = [p.chapel.u0 + 3.2 * p.scale, p.chapel.vc];
  const bellRing = ringPoints(centre, 0.7, 8, Math.PI / 8), y0 = p.chapel.ridgeY - 0.4, y1 = 15.1;
  bellRing.forEach((a, i) => {
    const b = bellRing[(i + 1) % bellRing.length]!, width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    rectWall(builder, styles, p.frame, [a, b], [y0, y1], centredWindow(width, 0.9, 2.2, 0.48), y0);
  });
  horizontalCap(builder, styles.wall, p.frame, bellRing, y0, false);
  pyramidOverRing(builder, styles, p.frame, bellRing, y1, { plan: centre, y: 17.0 });
  octahedronSolid(builder, styles.wall, p.frame, planPoint(centre[0], 17.1, centre[1]), 0.17);
  const court = ringOf({ u0: p.centre[0] - 13 * p.scale, u1: p.centre[0] + 9.6 * p.scale,
    v0: p.centre[1] - 4.9 * p.scale, v1: p.centre[1] + 1.9 * p.scale });
  const garden: VertexStyle = { ...styles.wall, color: colors.garden, surface: SURFACE.garden };
  prismSolid(builder, garden, p.frame, court, -0.08, 0.01, { top: true, bottom: false });
  return builder.triangleCount - before;
}

/** Ground collision follows the ward/chapel/wall components. The 2.8 m gate and court are
 * left walkable instead of treating the full hospital reservation as a solid box. */
export function leprozenhuisObstacles(landmark: Landmark): readonly Ring[] {
  const p = leprozenhuisPlan(landmark);
  const piers = [ { ...p.gate, v1: p.passage[0] }, { ...p.gate, v0: p.passage[1] } ];
  const outline = vesselOutline(p.chapel);
  const buttresses = outline.slice(1, -1).map((at): Ring => {
    const d: Vec2 = [at[0] - p.chapel.chordU, at[1] - p.chapel.vc];
    const length = Math.hypot(...d), outward: Vec2 = [d[0] / length, d[1] / length];
    const along: Vec2 = [-outward[1], outward[0]], half = VESSEL.buttress.width / 2;
    const corner = (s: number, depth: number): Vec2 => [
      at[0] + along[0] * s * half + outward[0] * depth,
      at[1] + along[1] * s * half + outward[1] * depth ];
    return [corner(-1, -0.1), corner(1, -0.1), corner(1, VESSEL.buttress.depth),
      corner(-1, VESSEL.buttress.depth)];
  });
  return [...[...p.wards, ...p.walls, ...piers].map(ringOf), outline, ...buttresses]
    .map((ring) => ring.map((q) => toLayout(p.plan, q)));
}
